import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchValuationReport,
  toVsjResult,
  buildResultFactors,
  fmtPrice,
  BACKEND_TYPE_DISPLAY,
  FACTOR_ICONS,
} from './vsj-result-api';
import type { ValuationReportDto, VsjResultData, ValuationResultDto } from './vsj-result-api';
import type { PlaceValueAvailable } from '../place-value-api';
import {
  IconPin, IconArrowLeft, ConfidenceBars,
} from './vsj-icons';
import './vsj-experience.css';

// ── Types ─────────────────────────────────────────────────────────────────────

type PageStatus = 'loading' | 'loaded' | 'error';

// ── Constants ─────────────────────────────────────────────────────────────────

const CONFIDENCE_NOTE: Record<1 | 2 | 3, string> = {
  3: 'El área tiene buena cobertura de datos. El rango es confiable como referencia.',
  2: 'Hay referencias en el área, aunque con menos datos de los que nos gustaría.',
  1: 'Pocos datos disponibles en este entorno. Tomá el rango como orientación general.',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function getReportUrl(token: string): string {
  return `${window.location.origin}/p/valor-san-juan/informe/${token}`;
}

// ── Report result body ────────────────────────────────────────────────────────

function ReportResultBody({
  snapshot,
  result,
  reportToken,
  createdAt,
}: {
  snapshot:    ValuationResultDto;
  result:      VsjResultData;
  reportToken: string;
  createdAt:   string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  const typeLabel      = BACKEND_TYPE_DISPLAY[snapshot.session.propertyType] ?? snapshot.session.propertyType;
  const surface        = snapshot.session.areaBuiltM2 ?? snapshot.session.areaLandM2 ?? 0;
  const locationText   = snapshot.session.streetLabel ?? 'Dirección confirmada';
  const resultFactors  = buildResultFactors(
    snapshot.streetTypeLabel,
    result,
    snapshot.session.propertyType,
  );

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(getReportUrl(reportToken));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent */
    }
  }

  let reveal = 0;
  const revealStyle = () => ({ animationDelay: `${(reveal++) * 90}ms` });

  return (
    <div className="vsj-result-body">
      {/* Location */}
      <div className="vsj-result-location-chip vsj-reveal" style={revealStyle()}>
        <IconPin size={15} />
        {locationText}
      </div>

      {/* Evidence */}
      <section className="vsj-result-evidence vsj-reveal" style={revealStyle()}>
        <p className="vsj-section-label">Lo que encontramos</p>
        <p className="vsj-result-observation">
          <strong>{result.observationCount} referencias</strong> de los últimos meses a menos
          de {result.radiusM} metros, con características similares.
        </p>
        <div className="vsj-result-factors">
          {resultFactors.map((f) => {
            const Icon = FACTOR_ICONS[f.icon];
            return (
              <div key={f.label} className="vsj-result-factor-row">
                <span className="vsj-result-factor-icon"><Icon size={17} /></span>
                {f.label}
              </div>
            );
          })}
        </div>
      </section>

      {/* Lead-in */}
      <p className="vsj-result-leadin vsj-reveal" style={revealStyle()}>
        Con eso, para {typeLabel} de {surface} m² en este entorno:
      </p>

      {/* Range */}
      <div className="vsj-result-range-block vsj-reveal" style={revealStyle()}>
        <div className="vsj-range-header">
          <span className="vsj-range-label">Rango de referencia</span>
          <span className="vsj-range-confidence">
            <ConfidenceBars level={result.confidenceLevel} />
            {result.confidenceLabel}
          </span>
        </div>
        <div className="vsj-range-value">
          <span className="vsj-range-currency">{result.currency}</span>
          {fmtPrice(result.totalLow)}
          <span className="vsj-range-sep"> — </span>
          {fmtPrice(result.totalHigh)}
        </div>
        {result.pricePerM2Low !== null && result.pricePerM2High !== null && (
          <p className="vsj-range-per-m2">
            {result.currency} {fmtPrice(result.pricePerM2Low)} — {fmtPrice(result.pricePerM2High)} por m²
          </p>
        )}
      </div>

      {/* Confidence note */}
      <p className="vsj-confidence-note vsj-reveal" style={revealStyle()}>
        {CONFIDENCE_NOTE[result.confidenceLevel as 1 | 2 | 3]}
      </p>

      {/* Honesty */}
      <p className="vsj-result-honesty vsj-reveal" style={revealStyle()}>
        Este rango refleja lo que el entorno y las referencias disponibles nos dejan ver.
        No conoce el estado interior, las reformas ni la historia de tu propiedad —
        eso puede mover el valor dentro del rango, o fuera de él.
      </p>

      <p className="vsj-disclaimer vsj-reveal" style={revealStyle()}>
        Referencia orientativa. No reemplaza una tasación profesional.
      </p>

      {/* Report metadata + share */}
      <div className="vsj-report-meta vsj-reveal" style={revealStyle()}>
        <p className="vsj-report-meta-date">
          Informe generado el {formatCreatedAt(createdAt)}
        </p>
        <button
          className="vsj-btn-outline"
          onClick={() => void handleCopy()}
          aria-label="Copiar link del informe"
        >
          {copied ? 'Link copiado' : 'Copiar link'}
        </button>
        <a
          className="vsj-btn-text vsj-btn-text--muted"
          href="/p/valor-san-juan/asistente"
        >
          Valorar otra propiedad
        </a>
      </div>
    </div>
  );
}

// ── Insufficient data body ─────────────────────────────────────────────────────

function ReportUnavailableBody({
  snapshot,
  createdAt,
}: {
  snapshot:    ValuationResultDto;
  createdAt:   string;
}): JSX.Element {
  const locationText = snapshot.session.streetLabel ?? 'Dirección confirmada';
  return (
    <div className="vsj-result-body vsj-result-unavailable">
      <div className="vsj-result-location-chip vsj-reveal">
        <IconPin size={15} />
        {locationText}
      </div>
      <div className="vsj-unavailable-content vsj-reveal" style={{ animationDelay: '80ms' }}>
        <h2 className="vsj-unavailable-title">
          Sin referencia disponible para este lugar
        </h2>
        <p className="vsj-unavailable-body">
          Al momento de generar este informe no había suficientes referencias
          públicas recientes para estimar un rango con responsabilidad en este entorno.
        </p>
      </div>
      <div className="vsj-report-meta vsj-reveal" style={{ animationDelay: '160ms' }}>
        <p className="vsj-report-meta-date">
          Informe generado el {formatCreatedAt(createdAt)}
        </p>
        <a className="vsj-btn-text" href="/p/valor-san-juan/asistente">
          Valorar otra propiedad
        </a>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function VsjReportPage(): JSX.Element {
  const { reportToken } = useParams<{ reportToken: string }>();

  const [pageStatus,  setPageStatus]  = useState<PageStatus>('loading');
  const [reportData,  setReportData]  = useState<ValuationReportDto | null>(null);
  const [resultData,  setResultData]  = useState<VsjResultData | null>(null);

  useEffect(() => {
    if (!reportToken) { setPageStatus('error'); return; }

    void fetchValuationReport(reportToken)
      .then((data) => {
        const snapshot = data.snapshot;
        if (snapshot.placeValue.status === 'available') {
          const available = snapshot.placeValue as PlaceValueAvailable;
          const group = available.market.groups[0];
          if (group) {
            const surface = snapshot.session.areaBuiltM2 ?? snapshot.session.areaLandM2 ?? 0;
            setResultData(toVsjResult(group, surface, available.place.contextRadiusM));
          }
        }
        setReportData(data);
        setPageStatus('loaded');
      })
      .catch(() => setPageStatus('error'));
  }, [reportToken]);

  // ── Loading ────────────────────────────────────────────────────────────────

  if (pageStatus === 'loading') {
    return (
      <div className="vsj-shell">
        <div className="vsj-frame">
          <div className="vsj-step vsj-result-step">
            <header className="vsj-step-header">
              <span className="vsj-section-label">Informe</span>
            </header>
            <div className="vsj-result-loading">
              <div className="vsj-result-loading-pulse" />
              <p className="vsj-result-loading-label">Cargando informe…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (pageStatus === 'error' || !reportData) {
    return (
      <div className="vsj-shell">
        <div className="vsj-frame">
          <div className="vsj-step vsj-result-step">
            <header className="vsj-step-header">
              <a href="/p/valor-san-juan/asistente" className="vsj-back-btn" aria-label="Volver">
                <IconArrowLeft size={22} />
              </a>
              <span className="vsj-section-label">Informe</span>
            </header>
            <div className="vsj-result-body vsj-result-unavailable">
              <div className="vsj-unavailable-content vsj-reveal">
                <h2 className="vsj-unavailable-title">Informe no encontrado</h2>
                <p className="vsj-unavailable-body">
                  Este informe puede haber expirado o el link es inválido.
                </p>
              </div>
              <div className="vsj-result-actions vsj-reveal" style={{ animationDelay: '80ms' }}>
                <a className="vsj-btn-primary" href="/p/valor-san-juan/asistente">
                  Nueva valuación
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Loaded ─────────────────────────────────────────────────────────────────

  const { snapshot, reportToken: rToken, createdAt } = reportData;
  const isAvailable = snapshot.placeValue.status === 'available' && resultData !== null;

  return (
    <div className="vsj-shell">
      <div className="vsj-frame">
        <div className="vsj-step vsj-result-step">
          <header className="vsj-step-header">
            <a href="/p/valor-san-juan/asistente" className="vsj-back-btn" aria-label="Nueva valuación">
              <IconArrowLeft size={22} />
            </a>
            <span className="vsj-section-label">Informe</span>
          </header>

          {isAvailable ? (
            <ReportResultBody
              snapshot={snapshot}
              result={resultData}
              reportToken={rToken}
              createdAt={createdAt}
            />
          ) : (
            <ReportUnavailableBody snapshot={snapshot} createdAt={createdAt} />
          )}
        </div>
      </div>
    </div>
  );
}
